// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Slope small multiples, one panel per jurisdiction. Left point = a carrier's
// share of the towers it built commercially; right point = its share of the
// towers the public helped pay for. The SLOPE is the finding.
//
// A ranked bar chart would put Telstra on top of both columns and say nothing —
// the interesting result here is a ratio near or far from 1, not a tallest bar.

import type { TowerData, Capture } from '../types';
import { CARRIER_ALIAS, CARRIER_COLOR } from '../colors';
import { attr, esc, num, pct } from '../format';
import { gloss } from '../glossary';
import { legend, viewHead, onActivate } from './ui';

export function mount(el: HTMLElement, data: TowerData): void {
  const panels = [...data.capture].sort((a, b) => {
    if (a.jurisdiction === 'Australia') return -1;
    if (b.jurisdiction === 'Australia') return 1;
    return spread(b) - spread(a);
  });

  const nat = data.capture.find((c) => c.jurisdiction === 'Australia')!;

  el.innerHTML = `
    ${viewHead({
      title: 'Who captured the public money',
      question: 'Did each network win public funding in proportion to the network it already runs?',
      blurb: `Each panel holds three lines. The left end is a carrier's share of Australia's <em>commercially</em> built towers; the right end is its share of the ${gloss('cofunded', 'publicly co-funded')} ones. A flat line means the money followed the market. A line sloping up means that carrier won more than its size implies — its ${gloss('capture', 'capture ratio')}.`,
    })}
    <div class="capture-lead">
      ${nat.rows
        .map(
          (r) => `<div class="cap-card" style="--c:${CARRIER_COLOR[r.mno]}">
            <span class="cap-name">${esc(CARRIER_ALIAS[r.mno])}</span>
            <span class="cap-ratio ${r.ratio && r.ratio >= 1.25 ? 'up' : r.ratio && r.ratio <= 0.8 ? 'down' : ''}">${r.ratio ? `${r.ratio.toFixed(2)}×` : '—'}</span>
            <span class="cap-sub">${pct(r.commercialShare, 0)} of commercial towers → ${pct(r.fundedShare, 0)} of funded ones</span>
          </div>`,
        )
        .join('')}
    </div>
    <div class="slope-grid">
      ${panels.map((p) => slopePanel(p)).join('')}
    </div>
    ${legend(data.meta.carriers.map((c) => ({ color: CARRIER_COLOR[c], label: CARRIER_ALIAS[c] })))}
    <p class="src-note">Jurisdictions with fewer than 10 co-funded towers are omitted — a share computed over a handful of sites is noise. Panels after Australia are ordered by how lopsided they are. Line thickness reflects the number of co-funded towers behind it, so a steep slope over three towers reads as thin.</p>
  `;

  el.querySelectorAll('[data-jur]').forEach((p) => {
    onActivate(p, () => {
      const j = p.getAttribute('data-jur')!;
      location.hash = `view=ignored&state=${encodeURIComponent(j)}`;
    });
  });
}

function spread(c: Capture): number {
  const rs = c.rows.filter((r) => r.ratio !== null).map((r) => Math.abs(Math.log(r.ratio!)));
  return rs.length ? Math.max(...rs) : 0;
}

function slopePanel(c: Capture): string {
  const W = 240;
  const H = 190;
  const P = { t: 34, b: 34, l: 44, r: 44 };
  const ih = H - P.t - P.b;
  const y = (share: number) => P.t + ih - share * ih;
  const xL = P.l;
  const xR = W - P.r;

  const lines = c.rows
    .map((r) => {
      const y0 = y(r.commercialShare);
      const y1 = y(r.fundedShare);
      const wid = Math.max(1.4, Math.min(6, Math.sqrt(r.funded) * 0.6));
      const tip = [
        `${CARRIER_ALIAS[r.mno]} — ${c.jurisdiction}`,
        `${num(r.commercial)} commercial towers (${pct(r.commercialShare)} of the jurisdiction's)`,
        `${num(r.funded)} co-funded towers (${pct(r.fundedShare)})`,
        r.ratio ? `Capture ratio ${r.ratio.toFixed(2)}×` : 'No commercial towers to compare',
      ].join('\n');
      return `<g class="slope" data-tip="${attr(tip)}">
        <line x1="${xL}" y1="${y0.toFixed(1)}" x2="${xR}" y2="${y1.toFixed(1)}" stroke="${CARRIER_COLOR[r.mno]}" stroke-width="${wid.toFixed(1)}" stroke-linecap="round" opacity="0.85" />
        <circle cx="${xL}" cy="${y0.toFixed(1)}" r="3.6" fill="${CARRIER_COLOR[r.mno]}" />
        <circle cx="${xR}" cy="${y1.toFixed(1)}" r="3.6" fill="${CARRIER_COLOR[r.mno]}" />
      </g>`;
    })
    .join('');

  const grid = [0, 0.25, 0.5, 0.75, 1]
    .map(
      (t) =>
        `<line x1="${xL}" x2="${xR}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" stroke="var(--border-subtle)" />
         <text x="${xL - 6}" y="${(y(t) + 3.5).toFixed(1)}" class="ax-sm" text-anchor="end">${t * 100}%</text>`,
    )
    .join('');

  return `<figure class="slope-panel" data-jur="${attr(c.jurisdiction)}" role="button" tabindex="0">
    <figcaption>${esc(c.jurisdiction)}<span>${num(c.fundedTotal)} co-funded</span></figcaption>
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Share of commercial versus co-funded towers by carrier in ${esc(c.jurisdiction)}">
      ${grid}${lines}
      <text x="${xL}" y="${H - 12}" class="ax-sm" text-anchor="middle">built</text>
      <text x="${xR}" y="${H - 12}" class="ax-sm" text-anchor="middle">funded</text>
    </svg>
  </figure>`;
}
